import { RouterState, createStore } from '@oneappexchange/lwc-wired';
import { State } from 'zustand';
import { BUCKETS, IS_DEVELOPMENT, KWL_SEARCH_PRELOAD_PAGE_SIZE } from '~/modules/config';
import { FeatureFlags } from '~/modules/feature_flags';
import { isConsistentFiltersEnabled, isSlackListingsEnabled } from '~/modules/utilities/slackPilot';
import { normalizeAgenticContentFromUrl } from '~/modules/helpers/agenticContentParamHelper';
import {
    EXTERNAL_AGENT_TECHNOLOGIES,
    SKILL_DOMAINS,
    isExternalAgentSearchExperienceAvailable,
    isPluginOnlyExternalAgentTechnology,
} from '~/modules/helpers/externalAgentSearchHelper';
import { getOrCreateExternalAgentSearchSeed } from '~/modules/helpers/externalAgentSearchSeedHelper';
import { parseAchievementParam, parseFilterParam, parseScalarFilterParam } from '~/modules/helpers/filterHelper';
import {
    getConsultantDefaultExpand,
    isConsultantAchievementFilterEnabled,
} from '~/modules/helpers/consultantAchievementFilterHelper';
import { getEnabledPriceFilters } from '~/modules/helpers/privateOfferFilterHelper';
import { CONSULTING_SEARCH_CONTEXT } from '~/modules/helpers/searchContextHelper';
import { EXPLORE_BASE_ROUTE, Routes } from '~/modules/routes';
import { FeatureFlagStoreStatus, featureFlagsStore, selectStatus } from '~/modules/stores/feature_flags_store';
import { routerStore } from '~/modules/stores/routerStore';
import { sanitizeText } from '~/modules/utilities/html_utilities';
import { SearchContext, SearchContextListing, SearchResult, SearchResultListing } from '~/schemas/search-schema-v1';
import { getKeywordlessExternalAgentSearchResults, getQueryCount, getSearchResults } from '../services/searchService';
import { handleException } from '~/modules/utilities/error_utilities';

export type SearchTotals = {
    apps: number;
    consultants: number;
    agents: number;
    slack?: number;
    externalAgents?: number;
};

export enum SearchType {
    APPS = 'apps',
    CONSULTANTS = 'consultants',
    AGENTS = 'agents',
    SLACK = 'slack',
    EXTERNAL_AGENTS = 'externalAgents',
}

type FilterOptions = {
    rating?: string;
    price?: string[];
    category?: string[];
    tech?: string[];
    domain?: string[];
    features?: string[]; // Features & Capabilities
    edition?: string[];
    languages?: string[];
    sfExpertise?: string[];
    industryExpertise?: string[];
    achievement?: string[];
    experts?: string[];
    location?: string;
    product?: string;
    impact?: string[];
    industry?: string[];
    persona?: string[];
    labs?: string[];
    agenticContent?: string[];
};

export type FilterParamOptions = keyof FilterOptions;

type RouteParams = {
    keywords?: string;
    type?: SearchType;
    sort?: string;
    rankingInformation?: string;
    expand?: string;
    searchSource?: string;
    context?: string;
} & { [k in FilterParamOptions]?: string };

export class FilterParams {
    rating?: string;
    price?: string[];
    category?: string[];
    tech?: string[];
    domain?: string[];
    features?: string[]; // Features & Capabilities
    edition?: string[];
    languages?: string[];
    sfExpertise?: string[];
    industryExpertise?: string[];
    achievement?: string[];
    experts?: string[];
    location?: string;
    product?: string;
    impact?: string[];
    industry?: string[];
    persona?: string[];
    labs?: string[];
    agenticContent?: string[];

    constructor() {
        return {
            rating: undefined,
            price: undefined,
            category: undefined,
            tech: undefined,
            domain: undefined,
            features: undefined,
            edition: undefined,
            languages: undefined,
            sfExpertise: undefined,
            industryExpertise: undefined,
            achievement: undefined,
            experts: undefined,
            location: undefined,
            product: undefined,
            impact: undefined,
            industry: undefined,
            persona: undefined,
            labs: undefined,
            agenticContent: undefined,
        };
    }
}

export type SearchParams = {
    keyword?: string;
    sort?: string;
    type?: string;
    filterParams?: FilterParams;
    rankingInformation?: string;
    isExploreSalesforceLabs?: boolean;
    isExploreAgentforce?: boolean;
    isConsulting?: boolean;
    expand?: string[];
    searchQueryId?: string;
    prevSearchQueryId?: string;
    searchSource?: string;
    seed?: number;
};

export interface SearchState extends State {
    searchResults: Map<string, SearchResult>;
    totals: Map<string, SearchTotals>;
    searchParams: SearchParams;
    currentPage: number;
}

const initialSearchParams = () => {
    return {
        keyword: '',
        filterParams: new FilterParams(),
        type: SearchType.APPS,
    };
};

const initialState: SearchState = {
    // searchKey -> SearchResults
    searchResults: new Map<string, SearchResult>(),
    // keyword -> SearchTotals
    totals: new Map<string, SearchTotals>(),
    searchParams: initialSearchParams(),
    currentPage: 0,
};

const initialSearchResult = (): SearchResult => ({
    // @ts-expect-error * Automated Type Error Fix
    id: undefined,
    isLoading: false,
    isError: false,
    isEmpty: false,
    listings: [],
    totalSearchResults: 0,
    // @ts-expect-error * Automated Type Error Fix
    context: undefined,
});

const mergeUniqueListings = (
    previousListings: SearchResultListing[],
    nextListings: SearchResultListing[],
): SearchResultListing[] => {
    const seenListingIds = new Set<string>();
    return [...previousListings, ...nextListings].filter(({ oafId }) => {
        if (seenListingIds.has(oafId)) {
            return false;
        }
        seenListingIds.add(oafId);
        return true;
    });
};

const getFilterString = (searchParams: SearchParams): string => {
    const filterParams = {};
    // @ts-expect-error * Automated Type Error Fix
    for (const [filterKey, value] of Object.entries(searchParams?.filterParams)) {
        if (value) {
            // @ts-expect-error * Automated Type Error Fix
            filterParams[filterKey] = value;
        }
    }
    const orderedFilterParams = Object.keys(filterParams)
        .sort()
        .reduce((obj, key) => {
            // @ts-expect-error * Automated Type Error Fix
            obj[key] = filterParams[key];
            return obj;
        }, {});
    return (
        Object.keys(orderedFilterParams)
            // @ts-expect-error * Automated Type Error Fix
            .map((key) => key + '=' + orderedFilterParams[key])
            .join('&')
    );
};

export const getSearchKey = (searchParams: SearchParams): string => {
    let searchKey = `keywords=${searchParams.keyword}&type=${searchParams.type ?? SearchType.APPS}${searchParams.isConsulting ? '&context=consulting' : ''}${searchParams.isExploreAgentforce ? '&agentforce=true' : ''}`;
    if (searchParams.sort) {
        searchKey += `&sort=${searchParams.sort}`;
    }
    if (searchParams.seed !== undefined && searchParams.type === SearchType.EXTERNAL_AGENTS && !searchParams.keyword) {
        searchKey += `&seed=${searchParams.seed}`;
    }
    if (searchParams.filterParams) {
        const filterString = getFilterString(searchParams);
        if (filterString) {
            searchKey += `&${filterString}`;
        }
    }
    return searchKey;
};

/** Search tab types. */
const SEARCH_TAB_TYPES = ['apps', 'consultants', 'agents', 'slack', 'externalAgents'] as const;
type SearchTabType = (typeof SEARCH_TAB_TYPES)[number];
const CONSULTANT_SORT_OPTIONS = ['relevant', 'rating', 'experts', 'popularity'];

/** Returns the standard tab types other than the current top-level search type. */
const getOtherTabTypes = (type: SearchType): SearchTabType[] => {
    return SEARCH_TAB_TYPES.filter(
        (t) =>
            t !== type &&
            (t !== 'slack' || isSlackListingsEnabled()) &&
            (t !== 'externalAgents' || isExternalAgentSearchExperienceAvailable()),
    ) as SearchTabType[];
};

type TabCountRequest = { type: SearchTabType; promise: Promise<number | null> };

const getTabCountRequests = (
    previousTabCounts: SearchTotals | undefined,
    keyword: string | undefined,
    otherTabTypes: SearchTabType[],
    hasActiveFilters: boolean,
    signal?: AbortSignal,
    suppressErrorLogging?: boolean,
): TabCountRequest[] => {
    // Skip if no keyword, totals already cached, or filters are active (filtered counts
    // must not be stored as totals — see else branch in fetchSearchResults).
    if (!keyword || previousTabCounts || hasActiveFilters) {
        return [];
    }
    return otherTabTypes.map((type) => ({
        type,
        promise: getQueryCount(keyword, type, signal, suppressErrorLogging),
    }));
};

const buildTabCounts = (
    currentType: SearchType,
    currentCount: number,
    otherTabCounts: Array<{ type: SearchTabType; count: number | null }>,
): SearchTotals => {
    // Build map preserving null for slack (not fetched), converting to 0 for others
    const byType = new Map<string, number | null | undefined>();
    for (const { type, count } of otherTabCounts) {
        if (type === 'slack') {
            // Preserve null for slack to distinguish "not fetched" from "0 results"
            byType.set(type, count === null ? undefined : count);
        } else {
            // Convert null to 0 for other types (backward compatibility)
            byType.set(type, count ?? 0);
        }
    }
    byType.set(currentType, currentCount);
    const totals: SearchTotals = {
        apps: byType.get('apps') ?? 0,
        consultants: byType.get('consultants') ?? 0,
        agents: byType.get('agents') ?? 0,
    };
    // Only include slack when Slack Listings are enabled
    if (isSlackListingsEnabled()) {
        totals.slack = byType.get('slack') as number | undefined;
    }
    if (byType.has(SearchType.EXTERNAL_AGENTS)) {
        totals.externalAgents = byType.get(SearchType.EXTERNAL_AGENTS) ?? 0;
    }
    return totals;
};

export const selectSearchResults = (state: SearchState, searchKey: string): SearchResult | undefined => {
    return state.searchResults.get(searchKey);
};

export const selectSearchTotals = (state: SearchState, keyword: string): SearchTotals | undefined => {
    return state.totals.get(keyword);
};

export const hasFilters = (filterParams: FilterParams | undefined): boolean => {
    return Object.values(filterParams ?? {}).some((value) => {
        return Array.isArray(value) ? value.length > 0 : Boolean(value);
    });
};

/**
 * Flatten FilterParams into the `Record<string, string>` shape the
 * summarize service expects (multi-value filters become `;`-joined
 * strings; empty values are dropped).
 */
export const filterParamsToRecord = (filterParams: FilterParams | undefined): Record<string, string> => {
    const out: Record<string, string> = {};
    if (!filterParams) {
        return out;
    }
    for (const [key, value] of Object.entries(filterParams)) {
        if (value === undefined || value === null) {
            continue;
        }
        if (Array.isArray(value)) {
            if (value.length > 0) {
                out[key] = value.join(';');
            }
        } else if (typeof value === 'string' && value.length > 0) {
            out[key] = value;
        }
    }
    return out;
};

export const isKeywordLessWithNoFilters = (state: SearchState): boolean => {
    // A native External Agents type is a meaningful browse scope even without
    // a keyword or subtype filter.
    return (
        state.searchParams.type !== SearchType.EXTERNAL_AGENTS &&
        !state.searchParams.keyword &&
        !hasFilters(state.searchParams.filterParams)
    );
};

export type SearchEventContext = Omit<SearchContext, 'advancedQuery' | 'contextual'> & SearchContextListing;

export const selectSearchContext = (state: SearchState, searchKey: string, listingId: string): SearchEventContext => {
    if (searchKey && listingId) {
        const searchResult = state.searchResults.get(searchKey);
        // @ts-expect-error * Automated Type Error Fix
        const resultElement: SearchResultListing[] = searchResult.listings.filter(
            (srl: SearchResultListing) => srl.oafId === listingId,
        );
        if (resultElement && resultElement.length === 1) {
            const listingContext = resultElement[0].context;
            return {
                // TODO Test how load more influences this
                // @ts-expect-error * Automated Type Error Fix
                searchQueryUid: searchResult.context.searchQueryUid,
                // @ts-expect-error * Automated Type Error Fix
                queryPipeline: searchResult.context.queryPipeline,
                documentUri: listingContext.documentUri,
                documentUrl: listingContext.documentUrl,
                documentUriHash: listingContext.documentUriHash,
                documentPosition: listingContext.documentPosition,
                collection: listingContext.collection,
                rankingModifier: listingContext.rankingModifier,
                sourceName: listingContext.sourceName,
                // @ts-expect-error * Automated Type Error Fix
                originLevel1: searchResult.context.originLevel1,
                // @ts-expect-error * Automated Type Error Fix
                originLevel2: searchResult.context.originLevel2,
                // @ts-expect-error * Automated Type Error Fix
                language: searchResult.context.language,
                // @ts-expect-error * Automated Type Error Fix
                duration: searchResult.context.duration,
                // @ts-expect-error * Automated Type Error Fix
                numberOfResults: searchResult.context.numberOfResults,
                searchTerm: searchKey,
            } as SearchEventContext;
        }
    }
    // @ts-expect-error * Automated Type Error Fix
    return null;
};

// This is the place where we determine if we should attempt to fetch more results.
export const shouldAttemptFetch = (
    previousResult: SearchResult | undefined,
    page: number,
    pageSize: number,
): boolean => {
    const idealResultCount = (page + 1) * pageSize;
    const previousResultElements = previousResult?.listings || [];
    return (
        !previousResult ||
        previousResult.isError ||
        (!previousResult.isLoading &&
            !previousResult.isEmpty &&
            previousResultElements.length < idealResultCount &&
            previousResultElements.length < previousResult.totalSearchResults)
    );
};

export const selectSearchParams = (state: SearchState): SearchParams => {
    return state.searchParams;
};

export const fromExplorePage = (state: SearchState): boolean => {
    return state.searchParams.keyword === '';
};

export const searchPageSelector = (state: SearchState) => {
    return {
        searchParams: selectSearchParams(state),
        results: selectSearchResults(state, getSearchKey(state.searchParams)),
        totals: selectSearchTotals(state, state.searchParams.keyword as string),
    };
};

export const exportPageSelector = (state: SearchState) => {
    return {
        searchParams: selectSearchParams(state),
        results: selectSearchResults(state, getSearchKey(state.searchParams)),
    };
};

export const searchStore = createStore(
    initialState,

    (set: SetState<SearchState>, get: GetState<SearchState>) => {
        let activeRequest: { controller: AbortController } | null = null;
        return {
            setError(key: string, error: unknown, previousResult: SearchResult | undefined) {
                set(function setError(draft) {
                    handleException(error, 'searchStore.setError', {
                        debugData: { key },
                    });
                    draft.searchResults.set(key, {
                        ...initialSearchResult(),
                        isError: true,
                        listings: previousResult?.listings || [],
                        totalSearchResults: previousResult?.totalSearchResults || 0,
                    });
                    return draft;
                });
            },
            setLoading(key: string, previousResult: SearchResult | undefined) {
                set(function setLoading(draft) {
                    const errorResults: SearchResult = {
                        ...initialSearchResult(),
                        isLoading: true,
                        listings: previousResult?.listings || [],
                        totalSearchResults: previousResult?.totalSearchResults || 0,
                    } as SearchResult;
                    draft.searchResults.set(key, errorResults);
                    return draft;
                });
            },
            async fetchSearchResults(searchParams: SearchParams, page = 0, pageSize = 12) {
                const type = (searchParams.type ?? SearchType.APPS) as SearchType;
                const keywordlessExternalAgentSeed =
                    type === SearchType.EXTERNAL_AGENTS && !searchParams.keyword
                        ? (searchParams.seed ?? getOrCreateExternalAgentSearchSeed())
                        : undefined;
                const effectiveSearchParams =
                    keywordlessExternalAgentSeed !== undefined
                        ? { ...searchParams, seed: keywordlessExternalAgentSeed }
                        : searchParams;
                const { keyword, sort, filterParams, rankingInformation, searchQueryId, prevSearchQueryId } =
                    effectiveSearchParams;
                const otherTabTypes = getOtherTabTypes(type);
                const searchKey = getSearchKey(effectiveSearchParams);
                const previousResult = selectSearchResults(get(), searchKey);
                const previousResultElements = previousResult?.listings || [];

                const controller = new AbortController();

                set(function updateSearchParams(draft: SearchState) {
                    draft.searchParams = effectiveSearchParams;
                    draft.currentPage = page;
                });

                // Check if this is an explore page (no keyword)
                const shouldPreload =
                    !keyword && !effectiveSearchParams.isConsulting && FeatureFlags.FF_DATA_CLOUD_SEARCH_UI;
                const effectivePageSize = shouldPreload ? pageSize * KWL_SEARCH_PRELOAD_PAGE_SIZE : pageSize;
                const effectivePage = shouldPreload ? Math.floor(page / KWL_SEARCH_PRELOAD_PAGE_SIZE) : page;

                if (!shouldAttemptFetch(previousResult, page, pageSize)) {
                    return;
                }

                if (activeRequest) {
                    activeRequest.controller.abort();
                }

                activeRequest = { controller };

                this.setLoading(searchKey, previousResult);

                const requestFilterParams: FilterParams = { ...filterParams };
                if (effectiveSearchParams.isExploreSalesforceLabs) {
                    requestFilterParams.labs = ['salesforceLabs'];
                }

                if (effectiveSearchParams.isExploreAgentforce && !requestFilterParams.product?.includes('Einstein 1')) {
                    requestFilterParams.product = requestFilterParams.product
                        ? `${requestFilterParams.product}, Einstein 1`
                        : 'Einstein 1';
                }

                // Derive active-filter state from requestFilterParams, not filterParams, so that
                // Explore Labs/Agentforce injected constraints (labs, product) are included.
                const hasActiveFilters = hasFilters(requestFilterParams);

                const previousTabCounts = keyword ? selectSearchTotals(get(), keyword) : undefined;
                // This store owns error classification for its own search/count requests below
                // (see the catch block's cancellation check and setError's handleException call) —
                // suppress the shared HTTP layer's logging so intentional cancellations from a new
                // search aborting this one don't also emit an error-level Splunk entry, and genuine
                // failures aren't logged twice.
                const tabCountRequests = getTabCountRequests(
                    previousTabCounts,
                    keyword,
                    otherTabTypes,
                    hasActiveFilters,
                    controller.signal,
                    true,
                );

                try {
                    const signal = controller.signal;
                    let searchRequest: Promise<SearchResult>;
                    if (keywordlessExternalAgentSeed !== undefined) {
                        searchRequest = getKeywordlessExternalAgentSearchResults({
                            page: effectivePage,
                            pageSize: effectivePageSize,
                            filterParams: requestFilterParams,
                            seed: keywordlessExternalAgentSeed,
                            sort,
                            rankingInformation: rankingInformation === 'true',
                            searchQueryId,
                            prevSearchQueryId,
                            signal,
                            suppressErrorLogging: true,
                        });
                    } else {
                        searchRequest = getSearchResults(
                            keyword,
                            type,
                            effectivePage,
                            effectivePageSize,
                            requestFilterParams,
                            sort,
                            rankingInformation === 'true',
                            searchQueryId,
                            prevSearchQueryId,
                            signal,
                            { suppressErrorLogging: true },
                        );
                    }

                    const [searchResult, ...tabCountsResolved] = await Promise.all([
                        searchRequest,
                        ...tabCountRequests.map((r) => r.promise),
                    ]);

                    const result: SearchResult = {
                        ...initialSearchResult(),
                        ...searchResult,
                        // A repeated upstream page must not create duplicate LWC keys and corrupt
                        // the card grid while the user continues browsing or starts a new search.
                        listings: mergeUniqueListings(previousResultElements, searchResult.listings),
                        isEmpty: searchResult.totalSearchResults === 0,
                    } as SearchResult;

                    set(function updateSearchResults(draft: SearchState) {
                        draft.searchResults.set(searchKey, result);
                        draft.searchParams.searchQueryId = searchResult?.context?.searchQueryUid;
                        draft.searchParams.prevSearchQueryId = undefined;

                        if (!keyword) {
                            return draft;
                        }

                        if (previousTabCounts) {
                            // When filters are active, preserve the unfiltered totals so
                            // platform filter counts continue to show total counts rather than
                            // filtered counts. The filtered count is already surfaced in the
                            // "Showing X of Y" display elsewhere on the page.
                            if (!hasActiveFilters) {
                                draft.totals.set(keyword, {
                                    ...previousTabCounts,
                                    [type]: result.totalSearchResults,
                                });
                            }
                        } else if (!hasActiveFilters) {
                            // Only store totals from an unfiltered search. A deep-link with
                            // filters has no previousTabCounts, but its result count is
                            // filtered — storing it would freeze a wrong count for all
                            // subsequent filter changes on the same keyword.
                            const otherTabCounts = tabCountRequests.map((req, i) => ({
                                type: req.type,
                                count: tabCountsResolved[i] as number | null,
                            }));
                            draft.totals.set(keyword, buildTabCounts(type, result.totalSearchResults, otherTabCounts));
                        }

                        return draft;
                    });
                } catch (error) {
                    // Axios yields 'CanceledError'; the underlying fetch/XHR transport can also
                    // surface a native 'AbortError' for the same intentional abort. Treat both as
                    // cancellations rather than routing either to setError.
                    if (error instanceof Error && (error.name === 'CanceledError' || error.name === 'AbortError')) {
                        set(function clearCanceledRequest(draft) {
                            draft.searchResults.delete(searchKey);
                        });
                        // Request was intentionally aborted - don't treat as error
                        // Just return silently, the new request will handle the state
                        return;
                    }
                    this.setError(searchKey, error, previousResult);
                } finally {
                    if (activeRequest?.controller === controller) {
                        activeRequest = null;
                    }
                }
            },
            setSearchParams(searchParams: SearchParams) {
                set(function setSearchParams(draft: SearchState) {
                    draft.searchParams = searchParams;
                });
            },
            setCurrentPage(currentPage: number) {
                set(function setCurrentPage(draft: SearchState) {
                    draft.currentPage = currentPage;
                });
            },
            reset() {
                set(function resetStore() {
                    return { ...initialState };
                });
            },
        };
    },
    'searchStore',
    IS_DEVELOPMENT,
);

const resolveSearchType = (params: RouteParams): SearchType => {
    // Returns SearchType.SLACK even when Slack Listings are off; the store
    // briefly holds Slack-tab state before setSearchStoreInfo redirects away. This is acceptable
    // because the routing layer (search page) gates tab visibility through isSlackListingsEnabled().
    if (params.type === SearchType.APPS && parseFilterParam(params.tech)?.includes('slack')) {
        return SearchType.SLACK;
    }

    if (params.type === SearchType.APPS) {
        return SearchType.APPS;
    }

    if (params.type === SearchType.AGENTS) {
        return SearchType.AGENTS;
    }

    if (params.type === SearchType.CONSULTANTS) {
        return SearchType.CONSULTANTS;
    }

    if (params.type === SearchType.EXTERNAL_AGENTS && isExternalAgentSearchExperienceAvailable()) {
        return SearchType.EXTERNAL_AGENTS;
    }

    return SearchType.APPS;
};

const normalizeSortForType = (sort: unknown, type: SearchType): string | undefined => {
    const normalizedSort = typeof sort === 'string' ? sort : undefined;
    return type === SearchType.EXTERNAL_AGENTS && normalizedSort === 'provider' ? undefined : normalizedSort;
};

/**
 * industryExpertise option values that were retired in the FF_CONSISTENT_FILTERS refactor.
 * Bookmarked consultant URLs may still reference these slugs; they must be discarded at
 * parse time so that filter state never holds stale values that have no matching option.
 */
const RETIRED_CONSULTANT_INDUSTRY_EXPERTISE_VALUES = new Set(['medias', 'psg']);

const normalizeConsultantIndustryExpertise = (
    values: string[] | undefined,
    resolvedType: string,
): string[] | undefined => {
    if (resolvedType !== SearchType.CONSULTANTS) {
        return values;
    }

    const normalizedValues = values?.filter((value) => !RETIRED_CONSULTANT_INDUSTRY_EXPERTISE_VALUES.has(value));

    return normalizedValues?.length ? normalizedValues : undefined;
};

/**
 * State/province selection was removed from the location filter in the FF_CONSISTENT_FILTERS
 * refactor. Bookmarked or legacy URLs may still carry a state-bearing location such as `US-*`
 * (wildcard state) or `US-CA` (specific state). In modern mode there is no state UI to represent
 * these, and current consultant listing data uses bare-country locations. Strip the suffix down to
 * the bare country at parse time for consultant routes whenever modern filters are active. Other
 * search types do not expose this filter, so their existing URL/API semantics remain untouched. The
 * legacy consultant state-selector flow keeps the `CC-*`/`CC-<state>` representation untouched.
 */
const normalizeConsultantLocation = (location: string | undefined, resolvedType: string): string | undefined => {
    if (resolvedType !== SearchType.CONSULTANTS || !location || !isConsistentFiltersEnabled()) {
        return location;
    }
    const [country] = location.split('-');
    return country || undefined;
};

const getFilterParams = (params: RouteParams | null, resolvedType: string) => {
    const filterParams = new FilterParams();
    if (params) {
        const techValues = parseFilterParam(params.tech);
        if (resolvedType === SearchType.EXTERNAL_AGENTS) {
            const externalAgentTech = [
                ...new Set(
                    techValues?.filter((value) => (EXTERNAL_AGENT_TECHNOLOGIES as readonly string[]).includes(value)),
                ),
            ];
            filterParams.tech = externalAgentTech?.length ? externalAgentTech : undefined;

            const pluginOnly = isPluginOnlyExternalAgentTechnology(filterParams.tech);
            const domains = pluginOnly
                ? undefined
                : parseFilterParam(params.domain)?.filter((value) =>
                      (SKILL_DOMAINS as readonly string[]).includes(value),
                  );
            filterParams.domain = domains?.length ? domains : undefined;
            return filterParams;
        }

        filterParams.rating = parseScalarFilterParam(params.rating);
        filterParams.price = getEnabledPriceFilters(parseFilterParam(params.price));
        filterParams.category = parseFilterParam(params.category);
        // `type=agents` is the complete Agentforce scope. Ignore stale URL technology
        // filters here so every store consumer observes the same effective search;
        // searchService independently enforces force/copilot at the request boundary.
        if (resolvedType === SearchType.AGENTS) {
            filterParams.tech = undefined;
        } else if (resolvedType === SearchType.SLACK) {
            // When type=apps&tech=slack maps to the Slack tab, drop 'slack' from the tech filter
            // so it isn't double-applied (searchService adds it back when type===SLACK).
            const withoutSlack = techValues?.filter((t) => t !== 'slack');
            filterParams.tech = withoutSlack && withoutSlack.length > 0 ? withoutSlack : undefined;
        } else {
            const standardTech = techValues?.filter(
                (value) => !(EXTERNAL_AGENT_TECHNOLOGIES as readonly string[]).includes(value),
            );
            filterParams.tech = standardTech?.length ? standardTech : undefined;
        }
        filterParams.features = parseFilterParam(params.features);
        filterParams.edition = parseFilterParam(params.edition);
        filterParams.languages = parseFilterParam(params.languages);
        filterParams.sfExpertise = parseFilterParam(params.sfExpertise);
        filterParams.achievement = isConsultantAchievementFilterEnabled(resolvedType)
            ? parseAchievementParam(params.achievement)
            : undefined;
        filterParams.industryExpertise = normalizeConsultantIndustryExpertise(
            parseFilterParam(params.industryExpertise),
            resolvedType,
        );
        filterParams.experts = parseFilterParam(params.experts);
        filterParams.location = normalizeConsultantLocation(parseScalarFilterParam(params.location), resolvedType);
        filterParams.product = parseScalarFilterParam(params.product);
        filterParams.impact = parseFilterParam(params.impact);
        filterParams.industry = parseFilterParam(params.industry);
        filterParams.persona = parseFilterParam(params.persona);
        filterParams.labs = parseFilterParam(params.labs);
        const parsedAgenticContent = parseFilterParam(params.agenticContent);
        filterParams.agenticContent = normalizeAgenticContentFromUrl(parsedAgenticContent);
    }
    return filterParams;
};

const parseExploreSearchParams = (
    params: RouteParams,
    isExploreSalesforceLabs: boolean = false,
    isExploreAgentforce: boolean = false,
) => {
    const searchQueryId = searchStore.getState().searchParams.searchQueryId;
    const prevSearchQueryId = searchStore.getState().searchParams.prevSearchQueryId;
    if (!params) {
        searchStore.setSearchParams({
            ...initialSearchParams(),
            isExploreSalesforceLabs,
            isExploreAgentforce,
            prevSearchQueryId: searchQueryId ? searchQueryId : prevSearchQueryId,
        });
        return;
    }
    const resolvedType = resolveSearchType(params);
    const filterParams = getFilterParams(params, resolvedType);
    const searchParams: SearchParams = {
        type: resolvedType,
        sort: params.sort,
        filterParams,
        isExploreSalesforceLabs,
        isExploreAgentforce,
        prevSearchQueryId: searchQueryId ? searchQueryId : prevSearchQueryId,
    };

    searchStore.setSearchParams(searchParams);
};

const parseSearchPageSearchParams = (params: RouteParams) => {
    // Repeated params (`?keywords=a&keywords=b`) parse into an array, so these are only
    // sometimes strings despite their types. Treat any other shape as if the param were absent.
    const keyword = sanitizeText(typeof params.keywords === 'string' ? params.keywords.trim() : '');
    const expand = typeof params.expand === 'string' ? params.expand.trim() : '';
    const resolvedType = resolveSearchType(params);
    const shouldUseSeed = resolvedType === SearchType.EXTERNAL_AGENTS && !keyword;
    const seed = shouldUseSeed ? getOrCreateExternalAgentSearchSeed() : undefined;
    const filterParams = getFilterParams(params, resolvedType);
    const prevSearchParams = searchStore.getState().searchParams;
    const hasKeywordChanged = keyword !== prevSearchParams.keyword;
    const hasTypeChanged = resolvedType !== prevSearchParams.type;
    const requiresNewSearchQuery = hasKeywordChanged || hasTypeChanged || !keyword;
    const searchQueryId = searchStore.getState().searchParams.searchQueryId;
    const prevSearchQueryId = searchStore.getState().searchParams.prevSearchQueryId;
    const domain = window.stores?.SITE_CONFIG?.domain;
    const isConsultingSearch =
        resolvedType === SearchType.CONSULTANTS &&
        (domain === 'appexchange' || domain === 'appexchange-jp') &&
        params.context === CONSULTING_SEARCH_CONTEXT;

    const searchParams: SearchParams = {
        keyword: keyword,
        type: resolvedType,
        sort: normalizeSortForType(params.sort, resolvedType),
        rankingInformation: params.rankingInformation,
        searchSource: params.searchSource,
        seed,
        filterParams,
        ...(isConsultingSearch ? { isConsulting: true } : {}),
        expand: expand ? expand.split(',') : prevSearchParams.expand,
        searchQueryId: requiresNewSearchQuery ? undefined : searchQueryId,
        prevSearchQueryId: requiresNewSearchQuery ? (searchQueryId ? searchQueryId : prevSearchQueryId) : undefined,
    };

    searchStore.setSearchParams(searchParams);
};

const parseConsultingSearchParams = (params: RouteParams) => {
    const sort = typeof params.sort === 'string' ? params.sort : undefined;
    const filterParams = new FilterParams();
    filterParams.sfExpertise = parseFilterParam(params.sfExpertise);
    filterParams.achievement = isConsultantAchievementFilterEnabled(SearchType.CONSULTANTS)
        ? parseAchievementParam(params.achievement)
        : undefined;
    filterParams.industryExpertise = parseFilterParam(params.industryExpertise);
    filterParams.location = normalizeConsultantLocation(
        parseScalarFilterParam(params.location),
        SearchType.CONSULTANTS,
    );
    filterParams.rating = parseScalarFilterParam(params.rating);
    filterParams.experts = parseFilterParam(params.experts);
    searchStore.setSearchParams({
        keyword: '',
        type: SearchType.CONSULTANTS,
        sort: CONSULTANT_SORT_OPTIONS.includes(sort || '') ? sort : undefined,
        filterParams,
        isConsulting: true,
        expand: getConsultantDefaultExpand(),
    });
};

const isNativeConsultingRoute = () => {
    const domain = window.stores?.SITE_CONFIG?.domain;
    return domain === 'appexchange' || domain === 'appexchange-jp';
};

type SearchRouteState = Pick<RouterState, 'url' | 'params'>;

const applySearchRoute = ({ url, params }: SearchRouteState): void => {
    if (url.indexOf(EXPLORE_BASE_ROUTE) > -1) {
        const isExploreSalesforceLabs = url.indexOf(BUCKETS.EXPLORE_LABS) > -1;
        const isExploreAgentforce = url.indexOf(BUCKETS.EXPLORE_AGENTFORCE) > -1;
        parseExploreSearchParams(params as RouteParams, isExploreSalesforceLabs, isExploreAgentforce);
    } else if (url.indexOf(Routes.SEARCH) > -1) {
        parseSearchPageSearchParams((params as RouteParams) || {});
    } else if (url.indexOf(Routes.CONSULTING_HOME) > -1 && isNativeConsultingRoute()) {
        parseConsultingSearchParams((params as RouteParams) || {});
    } else {
        searchStore.reset();
    }
};

routerStore.subscribe(applySearchRoute, (state: RouterState) => ({
    url: state.url,
    params: state.params,
}));

// Route parsing can run while only server-injected flag defaults are available. Replaying the raw
// router params once client-context flags finish loading keeps the stored location representation in
// sync with the final consistent-filters cohort. Reading the raw params is important for the inverse
// transition too: if modern defaults stripped a state before the final legacy assignment arrived,
// the original state-bearing value can be restored.
let previousFlagsStatus = selectStatus(featureFlagsStore.getState());

featureFlagsStore.subscribe((flagsStatus) => {
    if (previousFlagsStatus !== FeatureFlagStoreStatus.LOADED && flagsStatus === FeatureFlagStoreStatus.LOADED) {
        applySearchRoute(routerStore.getState());
    }
    previousFlagsStatus = flagsStatus;
}, selectStatus);
